/* eslint-disable no-unused-vars */
import React from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Checkbox,
  Box,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { visuallyHidden } from '@mui/utils';

import { AgColor } from '../../Styles/AgColor';

import { styleMappings } from '../../Styles/AgTypography';
import type { Order } from './useTableControls';

interface SortableTableHeadProps {
  columns: GridColDef[];
  order: Order;
  orderBy?: string;
  onRequestSort: (property: string) => void;
  selectable?: boolean;
  singleSelect?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleSelectAll?: () => void;
  showExpand?: boolean;
}

const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  columns,
  order,
  orderBy,
  onRequestSort,
  selectable,
  singleSelect,
  allSelected,
  someSelected,
  onToggleSelectAll,
  showExpand,
}) => {
  const createSortHandler = (property: string) => () => onRequestSort(property);

  return (
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: AgColor.grey0,
          ...styleMappings['AP.H5.Bold'],
          height: 56,
        }}
      >
        {selectable && (
          <TableCell padding='checkbox' sx={{ width: 48, py: 1 }}>
            {singleSelect ? null : (
              <Checkbox
                color='primary'
                indeterminate={Boolean(someSelected) && !allSelected}
                checked={Boolean(allSelected)}
                onChange={onToggleSelectAll}
                inputProps={{ 'aria-label': 'select all' }}
              />
            )}
          </TableCell>
        )}
        {columns.map((col, idx) => {
          const headerAlign =
            (col as any).alignHeader || (col as any).align || 'left';
          const isRight = headerAlign === 'right';
          const isCenter = headerAlign === 'center';
          const justify = isCenter ? 'center' : 'flex-start';
          const flexDirection = isRight ? 'row-reverse' : 'row';
          return (
            <TableCell
              key={col.field as string}
              align={headerAlign as any}
              sortDirection={orderBy === (col.field as string) ? order : false}
              sx={{
                minWidth: col.minWidth || 'max-content',
                width: col.width || 80,
                py: 1,
                borderRight:
                  idx === 0 ? `1px solid ${AgColor.grey4}` : undefined,
                ...styleMappings['AP.H5.Bold'],
              }}
            >
              <Box
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: headerAlign as any,
                  '& .MuiSvgIcon-root': {
                    color: `${AgColor.secondaryDark} !important`,
                  },
                }}
              >
                {col.sortable ?? true ? (
                  <TableSortLabel
                    active={orderBy === (col.field as string)}
                    direction={
                      orderBy === (col.field as string) ? order : 'asc'
                    }
                    onClick={createSortHandler(col.field as string)}
                    color='primary'
                    sx={{
                      width: '100%',
                      display: 'inline-flex',
                      justifyContent: justify,
                      flexDirection,
                      '& .MuiTableSortLabel-icon': {
                        ml: isRight ? 0 : 0.5,
                        mr: isRight ? 0.5 : 0,
                      },
                    }}
                  >
                    <span>{col.headerName}</span>
                    {orderBy === (col.field as string) ? (
                      <span style={visuallyHidden as any}>
                        {order === 'desc'
                          ? 'sorted descending'
                          : 'sorted ascending'}
                      </span>
                    ) : null}
                  </TableSortLabel>
                ) : (
                  <span>{col.headerName}</span>
                )}
              </Box>
            </TableCell>
          );
        })}
        {showExpand && (
          <TableCell padding='checkbox' sx={{ width: 40, py: 1 }} />
        )}
      </TableRow>
    </TableHead>
  );
};

export default SortableTableHead;
